import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Info, RefreshCcw, Sparkles, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";

/* ---------------- Options ---------------- */
const COMPETITION_OPTIONS = [
  { id: "solo", label: "I am the only offer", weight: +10 },
  { id: "maybe", label: "I expect maybe another", weight: 0 },
  { id: "competitive", label: "I'm pretty sure it's going to be competitive", weight: -10 },
] as const;

const FINANCING_OPTIONS = [
  { id: "fha", label: "FHA — Least Strong / Requires Appraisal for All Homes", weight: -15 },
  { id: "va", label: "VA — Least Strong / Requires Appraisal for All Homes & WDI", weight: -12 },
  { id: "conv", label: "Conventional — Less Strong / Requires Appraisal for +$1M Homes", weight: 0 },
  { id: "cash", label: "Cash — Strongest / No Appraisal Needed", weight: +20 },
] as const;

const SALE_CONTINGENCY = [
  { id: "needToSell", label: "Yes — I must sell before I buy (Less strong)", weight: -12 },
  { id: "noSale", label: "No — I don't need to sell (Strong)", weight: +6 },
] as const;

const INSPECTION_OPTIONS = [
  { id: "full", label: "Full Inspection — Standard (right to negotiate/terminate)", weight: -6 },
  { id: "aLaCarte", label: "A La Carte — Specific tests only (e.g., radon, mold)", weight: -2 },
  { id: "asIs", label: "AS-IS (No Inspection) — Strong", weight: +10 },
  { id: "infoOnly", label: "Information Only — Depends on seller", weight: -1 },
] as const;

const APPRAISAL_OPTIONS = [
  { id: "yes", label: "Yes Appraisal — Least Strong", weight: -10 },
  { id: "gapCover", label: "Yes Appraisal, but guarantee to cover gap (price firm)", weight: +6 },
  { id: "no", label: "No Appraisal — Strongest", weight: +14 },
] as const;

const FINANCING_CONT = [
  { id: "yes", label: "Yes — If loan denied, I can terminate (Less strong)", weight: -8 },
  { id: "no", label: "No — Confident in approval (Stronger)", weight: +8 },
] as const;

const TAX_TITLE_SPLIT = [
  { id: "split", label: "Split 50/50 — Standard", weight: 0 },
  { id: "buyer100", label: "Buyer 100% — Strongest", weight: +8 },
] as const;

const TITLE_PREF = [
  { id: "buyerPref", label: "Use Buyer's Preferred Title Company — Standard", weight: 0 },
  { id: "sellerPref", label: "Use Seller's Preferred Title Company — Stronger", weight: +4 },
] as const;

const COMMISSION = [
  { id: "sellerPays", label: "Seller pays Buyer Agency Commission — Standard", weight: 0 },
  { id: "buyerPaysPortion", label: "Buyer contributes portion of Buyer Agency Commission — Stronger", weight: +6 },
] as const;

/* ---------------- Helpers ---------------- */
function clamp(n: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, n));
}
function labelForScore(score: number) {
  if (score >= 85) return { label: "Elite", emoji: "🚀" };
  if (score >= 70) return { label: "Strong", emoji: "💪" };
  if (score >= 55) return { label: "Competitive", emoji: "⚖️" };
  return { label: "Needs Work", emoji: "🧩" };
}
function getRecommendations(state: {
  competition: typeof COMPETITION_OPTIONS[number]["id"];
  financing: typeof FINANCING_OPTIONS[number]["id"];
  downPct: number;
  emdPct: number;
  inspection: typeof INSPECTION_OPTIONS[number]["id"];
  appraisal: typeof APPRAISAL_OPTIONS[number]["id"];
  finCont: typeof FINANCING_CONT[number]["id"];
  taxSplit: (typeof TAX_TITLE_SPLIT)[number]["id"] | null;
  titlePref: (typeof TITLE_PREF)[number]["id"] | null;
  commission: typeof COMMISSION[number]["id"];
  escalationPct: number;
}): string[] {
  const rec: string[] = [];

  if (state.competition !== "solo" && state.escalationPct === 0) {
    rec.push("Consider adding an escalation (e.g., +1–3%) in competitive situations.");
  }
  if (state.emdPct < 5) rec.push("Increase EMD to at least 5% to improve perceived commitment.");
  if (state.financing !== "cash" && state.downPct < 20) {
    rec.push("Target 20%+ down payment to reduce perceived financing risk.");
  }
  if (state.inspection !== "asIs" && state.competition === "competitive") {
    rec.push("Under heavy competition, consider a reduced-scope inspection or information-only.");
  }
  if (state.appraisal === "yes" && state.competition !== "solo") {
    rec.push("Guaranteeing a portion of any appraisal gap can lower seller uncertainty.");
  }
  if (state.titlePref !== "sellerPref") {
    rec.push("Using seller’s preferred title company can ease acceptance.");
  }
  if (state.commission !== "buyerPaysPortion" && state.competition === "competitive") {
    rec.push("Offering to contribute to buyer agency commission may improve attractiveness.");
  }
  if (rec.length === 0)
    rec.push("Your selections look balanced. Review timelines/clauses with your agent before drafting.");
  return rec.slice(0, 5);
}

/* ------------- Reusable UI: Option Tile ------------- */
function OptionTile({
  active,
  label,
  onSelect,
  fullWidth,
}: {
  active: boolean;
  label: string;
  onSelect: () => void;
  fullWidth?: boolean;
}) {
  const outerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 16,
    padding: "16px 18px",
    cursor: "pointer",
    outline: "none",
    transition: "background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .1s ease",
    width: fullWidth ? "100%" : undefined,
    border: "1px solid rgba(148,163,184,.50)",
    background: active ? "linear-gradient(135deg, rgba(129,140,248,0.24), rgba(56,189,248,0.18))" : "rgba(15,23,42,0.85)",
    boxShadow: active ? "0 0 0 1px rgba(196,181,253,0.9)" : "0 0 0 1px rgba(15,23,42,0.9)",
  };

  return (
    <div
      role="radio"
      aria-checked={active}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
      className="mc-pill"
      style={outerStyle}
    >
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <span
          aria-hidden
          style={{
            height: 22,
            width: 22,
            minWidth: 22,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "999px",
            border: active ? "1px solid #f9fafb" : "1px solid rgba(148,163,184,.8)",
            background: active ? "linear-gradient(135deg,#a855f7,#22c55e)" : "rgba(15,23,42,1)",
            color: active ? "#0b1120" : "#e5e7eb",
          }}
        >
          {active ? <CheckCircle2 size={16} color="#020617" /> : null}
        </span>
        <div style={{ width: 24, flex: "0 0 24px" }} />
        <span style={{ lineHeight: 1.45, fontSize: 15, color: "#e5e7eb" }}>{label}</span>
      </div>
    </div>
  );
}

/* --------- Reusable UI: Form Row --------- */
function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(160px, 240px) 1fr",
        alignItems: "center",
        columnGap: 18,
        rowGap: 10,
        width: "100%",
      }}
    >
      <Label className="text-neutral-200" style={{ margin: 0 }}>
        {label}
      </Label>
      <div>{children}</div>
    </div>
  );
}

function SummaryKV({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ opacity: 0.7, fontSize: 12, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function App() {
  const [vw, setVw] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 0);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isWide = vw >= 1024;

  // 0 — Competition
  const [competition, setCompetition] = useState<(typeof COMPETITION_OPTIONS)[number]["id"]>("maybe");

  // 1 — Basics
  const [propertyAddress, setPropertyAddress] = useState("");
  const [buyerNames, setBuyerNames] = useState("");
  const [settlementDate, setSettlementDate] = useState("");
  const [totalCash, setTotalCash] = useState<string>("");
  const [notes, setNotes] = useState("");

  // 2 — Financing
  const [financing, setFinancing] = useState<(typeof FINANCING_OPTIONS)[number]["id"]>("conv");
  const [downPct, setDownPct] = useState<number>(20);

  // 3 — Home Sale Contingency
  const [saleCont, setSaleCont] = useState<(typeof SALE_CONTINGENCY)[number]["id"]>("noSale");

  // 4 — EMD
  const [emdPct, setEmdPct] = useState<number>(5);

  // 5 — Inspections
  const [inspection, setInspection] = useState<(typeof INSPECTION_OPTIONS)[number]["id"]>("aLaCarte");
  const [inspectionChecks, setInspectionChecks] = useState<string[]>([]);

  // 6 — Appraisal
  const [appraisal, setAppraisal] = useState<(typeof APPRAISAL_OPTIONS)[number]["id"]>("yes");
  const [gapAmount, setGapAmount] = useState<number>(0);

  // 7 — Financing Contingency
  const [finCont, setFinCont] = useState<(typeof FINANCING_CONT)[number]["id"]>("yes");

  // 8 — Taxes/Title (nullable to allow toggle-off)
  const [taxSplit, setTaxSplit] = useState<(typeof TAX_TITLE_SPLIT)[number]["id"] | null>("split");
  const [titlePref, setTitlePref] = useState<(typeof TITLE_PREF)[number]["id"] | null>("buyerPref");

  // 9 — Commission
  const [commission, setCommission] = useState<(typeof COMMISSION)[number]["id"]>("sellerPays");

  // 10 — Escalation
  const [escalationPct, setEscalationPct] = useState<number>(0);

  // Score
  const score = useMemo(() => {
    let s = 60;

    s += COMPETITION_OPTIONS.find((o) => o.id === competition)!.weight;
    s += FINANCING_OPTIONS.find((o) => o.id === financing)!.weight;
    s += Math.min(20, Math.max(0, (downPct - 10) * 0.6));
    s += SALE_CONTINGENCY.find((o) => o.id === saleCont)!.weight;

    if (emdPct >= 10) s += 12;
    else if (emdPct >= 5) s += 6;
    else if (emdPct >= 2) s += 2;
    else s -= 4;

    s += INSPECTION_OPTIONS.find((o) => o.id === inspection)!.weight;
    s += APPRAISAL_OPTIONS.find((o) => o.id === appraisal)!.weight;
    if (appraisal === "gapCover") s += Math.min(10, Math.floor(gapAmount / 5000));

    s += FINANCING_CONT.find((o) => o.id === finCont)!.weight;

    const titlePrefOpt = TITLE_PREF.find((o) => o.id === titlePref);
    if (titlePrefOpt) s += titlePrefOpt.weight;

    const taxSplitOpt = TAX_TITLE_SPLIT.find((o) => o.id === taxSplit);
    if (taxSplitOpt) s += taxSplitOpt.weight;

    s += COMMISSION.find((o) => o.id === commission)!.weight;

    if (competition !== "solo") {
      s += Math.min(6, Math.max(0, Math.round(escalationPct * 0.6)));
    }

    return clamp(Math.round(s));
  }, [
    competition,
    financing,
    downPct,
    saleCont,
    emdPct,
    inspection,
    appraisal,
    gapAmount,
    finCont,
    taxSplit,
    titlePref,
    commission,
    escalationPct,
  ]);

  const badge = useMemo(() => labelForScore(score), [score]);

  function resetAll() {
    setCompetition("maybe");
    setPropertyAddress("");
    setBuyerNames("");
    setSettlementDate("");
    setTotalCash("");
    setNotes("");
    setFinancing("conv");
    setDownPct(20);
    setSaleCont("noSale");
    setEmdPct(5);
    setInspection("aLaCarte");
    setInspectionChecks([]);
    setAppraisal("yes");
    setGapAmount(0);
    setFinCont("yes");
    setTaxSplit("split");
    setTitlePref("buyerPref");
    setCommission("sellerPays");
    setEscalationPct(0);
  }

  const recsState = {
    competition,
    financing,
    downPct,
    emdPct,
    inspection,
    appraisal,
    finCont,
    taxSplit,
    titlePref,
    commission,
    escalationPct,
  };

  const sectionAnchorStyle: React.CSSProperties = { scrollMarginTop: 96 };

  // Offer Strength Card
  function OfferStrengthCard({ compact }: { compact?: boolean }) {
    const [mobileMore, setMobileMore] = React.useState(false);
    const isCollapsed = !!compact && !mobileMore;

    const TopRow = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isCollapsed ? 4 : 8,
          alignItems: "center",
        }}
      >
        {/* score + label centered */}
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: isCollapsed ? 17 : compact ? 18 : 22,
              fontWeight: 700,
              lineHeight: 1.05,
            }}
          >
            {score}
          </div>
          <div
            style={{
              fontSize: isCollapsed ? 11 : compact ? 12 : 13,
              opacity: 0.9,
              marginTop: 1,
              whiteSpace: "nowrap",
            }}
          >
            {badge.label} {badge.emoji}
          </div>
        </div>

        {/* gauge with side padding */}
        <div style={{ width: "100%", padding: "0 12px", marginTop: isCollapsed ? 4 : 6 }}>
          <div
            className="mc-bar"
            style={{
              width: "100%",
              height: 8,
              borderRadius: 999,
              background: "rgba(15,23,42,0.9)",
            }}
          >
            <motion.div
              className="mc-bar-fill"
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ type: "spring", stiffness: 80, damping: 20 }}
              style={{
                height: "100%",
                borderRadius: 999,
                background: "linear-gradient(90deg,#22c55e,#a855f7,#38bdf8)",
              }}
            />
          </div>
        </div>

        {/* compact toggle */}
        {compact && (
          <button
            onClick={() => setMobileMore((v) => !v)}
            style={{
              fontSize: 11,
              padding: "4px 11px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,.5)",
              background: "rgba(15,23,42,.9)",
              display: "inline-flex",
              alignSelf: "center",
              alignItems: "center",
              gap: 4,
              whiteSpace: "nowrap",
              marginTop: isCollapsed ? 4 : 6,
            }}
            aria-expanded={mobileMore}
          >
            {mobileMore ? "Hide" : "More"}
          </button>
        )}
      </div>
    );

    const padding = isCollapsed ? "8px 14px 10px" : "20px 20px 22px";

    return (
      <Card
        className="mc-card"
        style={{
          background: "radial-gradient(circle at top,#1d2a4d,#020617)",
          border: "1px solid rgba(148,163,184,.6)",
        }}
      >
        <CardContent
          style={{
            padding,
            textAlign: compact ? "center" : "center",
          }}
        >
          {/* title */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: isCollapsed ? 4 : 10,
            }}
          >
            <h2
              className="text-xl md:text-2xl font-semibold"
              style={{
                whiteSpace: "nowrap",
                lineHeight: 1,
                color: "#E5E7EB",
                fontSize: isCollapsed ? 18 : undefined,
              }}
            >
              Offer Strength
            </h2>
            <Sparkles size={18} className="text-violet-300" />
          </div>

          {/* score + bar */}
          <div>{TopRow}</div>

          {/* collapsed(모바일 Hide)에서는 여기까지만 렌더링 */}
          {(!compact || mobileMore) && (
            <>
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: compact ? "1fr 1fr" : "repeat(2, minmax(0,1fr))",
                  maxWidth: 680,
                  margin: compact ? "14px 4px 0" : "18px auto 0",
                }}
              >
                <SummaryKV
                  label="Competition"
                  value={COMPETITION_OPTIONS.find((o) => o.id === competition)?.label || "Not selected"}
                />
                <SummaryKV
                  label="Financing"
                  value={`${
                    FINANCING_OPTIONS.find((o) => o.id === financing)?.label?.split(" — ")[0]
                  } • ${downPct}% down`}
                />
                <SummaryKV label="Appraisal" value={APPRAISAL_OPTIONS.find((o) => o.id === appraisal)?.label || ""} />
                <SummaryKV label="EMD" value={`${emdPct}% of offer`} />
                <SummaryKV label="Escalation" value={`${escalationPct}%`} />
              </div>

              <div style={{ marginTop: 18 }}>
                <p
                  className="text-sm font-medium"
                  style={{
                    marginBottom: 8,
                    textAlign: compact ? "left" : "center",
                    color: "#c4b5fd",
                  }}
                >
                  Recommendations
                </p>
                <ul
                  style={{
                    listStyle: "disc",
                    paddingLeft: 24,
                    margin: compact ? "0" : "0 auto",
                    textAlign: "left",
                    maxWidth: 680,
                    lineHeight: 1.6,
                  }}
                >
                  {getRecommendations(recsState).map((r, i) => (
                    <li key={i} style={{ fontSize: 14 }}>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <Button variant="secondary" className="mc-btn-secondary" onClick={resetAll}>
                  <RefreshCcw className="mr-2 h-4 w-4" /> Reset
                </Button>
                <Button
                  onClick={async () => {
                    const competitionLabel =
                      COMPETITION_OPTIONS.find((o) => o.id === competition)?.label || "Not selected";
                    const financingLabel =
                      FINANCING_OPTIONS.find((o) => o.id === financing)?.label?.split(" — ")[0] || "Not selected";
                    const saleContLabel =
                      SALE_CONTINGENCY.find((o) => o.id === saleCont)?.label || "Not selected";
                    const inspectionLabel =
                      INSPECTION_OPTIONS.find((o) => o.id === inspection)?.label || "Not selected";
                    const appraisalLabel =
                      APPRAISAL_OPTIONS.find((o) => o.id === appraisal)?.label || "Not selected";
                    const finContLabel =
                      FINANCING_CONT.find((o) => o.id === finCont)?.label || "Not selected";
                    const titlePrefLabel = titlePref
                      ? TITLE_PREF.find((o) => o.id === titlePref)?.label || "Not selected"
                      : "Not selected";
                    const taxSplitLabel = taxSplit
                      ? TAX_TITLE_SPLIT.find((o) => o.id === taxSplit)?.label || "Not selected"
                      : "Not selected";
                    const commissionLabel =
                      COMMISSION.find((o) => o.id === commission)?.label || "Not selected";

                    const formattedCash = totalCash ? `$${Number(totalCash).toLocaleString()}` : "Not specified";

                    const inspectionExtras =
                      inspection === "aLaCarte" && inspectionChecks.length
                        ? inspectionChecks
                            .map((id) =>
                              id
                                .split("-")
                                .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                                .join(" ")
                            )
                            .join(", ")
                        : "None";

                    const appraisalGap =
                      appraisal === "gapCover" && gapAmount > 0
                        ? `$${gapAmount.toLocaleString()}`
                        : appraisal === "gapCover"
                        ? "Up to buyer-defined amount"
                        : "N/A";

                    const recLines = getRecommendations(recsState)
                      .map((x, i) => `${i + 1}. ${x}`)
                      .join("\n");

                    const shareText = `Maison Collective — Offer Strategy Summary

Property: ${propertyAddress || "Not specified"}
Buyers: ${buyerNames || "Not specified"}
Preferred Settlement Date: ${settlementDate || "Not specified"}
Total Cash Available: ${formattedCash}
Notes for Agent: ${notes || "None"}

Offer Strength: ${score} – ${badge.label} ${badge.emoji}

KEY TERMS
- Competition: ${competitionLabel}
- Financing: ${financingLabel}
- Down Payment: ${downPct}%
- Home Sale Contingency: ${saleContLabel}
- Earnest Money Deposit (EMD): ${emdPct}% of offer
- Inspection: ${inspectionLabel}
- A-La-Carte Tests: ${inspectionExtras}
- Appraisal: ${appraisalLabel}
- Appraisal Gap Coverage: ${appraisalGap}
- Financing Contingency: ${finContLabel}
- Title Company Preference: ${titlePrefLabel}
- Recordation / Transfer Tax Split: ${taxSplitLabel}
- Commission: ${commissionLabel}
- Escalation Cap: ${escalationPct}%

RECOMMENDATIONS
${recLines || "Your selections look balanced. Review timelines/clauses with your agent before drafting."}
`;

                    try {
                      if (navigator.share) {
                        await navigator
                          .share({
                            title: "Maison Collective — Offer Strategy Summary",
                            text: shareText,
                            url: typeof window !== "undefined" ? window.location.href : undefined,
                          })
                          .catch((err) => {
                            if (err && (err as any).name === "AbortError") return;
                            navigator.clipboard.writeText(shareText);
                            alert("Sharing is not available. Copied your offer summary to the clipboard instead.");
                          });
                      } else {
                        await navigator.clipboard.writeText(shareText);
                        alert("Copied your offer summary to the clipboard. Paste it into a message or email to share.");
                      }
                    } catch {
                      await navigator.clipboard.writeText(shareText);
                      alert("Copied your offer summary to the clipboard. Paste it into a message or email to share.");
                    }
                  }}
                >
                  Share
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mc-bg" style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 20px 48px" }}>
        {/* Header */}
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              border: "1px solid rgba(148,163,184,.6)",
              background: "radial-gradient(circle at top,#1d2a4d,#020617)",
              padding: "10px 18px",
              borderRadius: 18,
              fontSize: 12,
              color: "#E5E7EB",
              marginBottom: 14,
              maxWidth: 700,
            }}
          >
            <span
              style={{
                letterSpacing: 0.12,
                fontSize: 11,
                textTransform: "uppercase",
                opacity: 0.9,
              }}
            >
              Education Notice
            </span>
            <span style={{ fontSize: 12, lineHeight: 1.4, textAlign: "center" }}>
              Created for the client’s purposes. Education provided for guidance only; results are not guaranteed.
            </span>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Maison Collective — Offer Strategy Simulator
          </h1>

          <p className="mt-1 text-sm text-neutral-400">
            Make your selections below. Your Offer Strength updates live.
          </p>
        </div>

        {/* mobile sticky */}
        {!isWide && (
          <div style={{ position: "sticky", top: 12, zIndex: 50, margin: "14px 0" }}>
            <OfferStrengthCard compact />
          </div>
        )}

        {/* layout */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isWide ? "1fr 340px" : "1fr",
            gap: isWide ? 24 : 20,
            alignItems: "start",
            marginTop: 24,
          }}
        >
          {/* Left: questions */}
          <div style={{ display: "grid", gap: isWide ? 28 : 22 }}>
            {/* 1. Basic Information */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h2 className="text-lg font-semibold text-violet-200">1. Basic Information</h2>
                </div>

                <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
                  <FormRow label="Property Address">
                    <Input
                      value={propertyAddress}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPropertyAddress(e.target.value)}
                      placeholder="123 Main St, City, ST"
                    />
                  </FormRow>
                  <FormRow label="Preferred Settlement Date">
                    <Input
                      type="date"
                      value={settlementDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettlementDate(e.target.value)}
                      style={{ backgroundColor: "#fff", color: "#111" }}
                    />
                  </FormRow>
                  <FormRow label="Buyers Names">
                    <Input
                      value={buyerNames}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuyerNames(e.target.value)}
                      placeholder="Jane & John Doe"
                    />
                  </FormRow>
                  <FormRow label="Total Cash Available ($)">
                    <Input
                      inputMode="numeric"
                      value={totalCash}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTotalCash(e.target.value.replace(/[^0-9]/g, ""))
                      }
                      placeholder="e.g., 160000"
                    />
                  </FormRow>
                  <FormRow label="Notes for My Agent">
                    <Textarea
                      value={notes}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                      placeholder="Renovation budget, timeline constraints, etc."
                    />
                  </FormRow>
                </div>
              </CardContent>
            </Card>

            {/* 2. Competition */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h2 className="text-lg font-semibold text-violet-200">2. Competition</h2>
                  <Info className="h-4 w-4 text-neutral-400" />
                </div>
                <p className="mt-1 text-sm text-neutral-400">
                  Is there a competition or are you the only offer? Choose one.
                </p>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr", marginTop: 14 }}>
                  {COMPETITION_OPTIONS.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={competition === opt.id}
                      label={opt.label}
                      onSelect={() => setCompetition(opt.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 3. Financing */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h2 className="text-lg font-semibold text-violet-200">3. Financing Method</h2>
                </div>

                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {FINANCING_OPTIONS.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={financing === opt.id}
                      label={opt.label}
                      onSelect={() => setFinancing(opt.id)}
                    />
                  ))}
                </div>

                <div style={{ marginTop: 16 }}>
                  <Label className="text-neutral-200">Down Payment (%)</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
                    <Slider
                      value={[downPct]}
                      min={0}
                      max={100}
                      step={1}
                      onValueChange={(v) => setDownPct(v[0])}
                      className="w-full"
                    />
                    <div style={{ width: 54, textAlign: "right", fontSize: 14 }}>{downPct}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 4. Home Sale Contingency */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">4. Home Sale Contingency</h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {SALE_CONTINGENCY.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={saleCont === opt.id}
                      label={opt.label}
                      onSelect={() => setSaleCont(opt.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 5. EMD */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">5. Earnest Money Deposit (EMD)</h2>
                <p className="text-sm text-neutral-400" style={{ marginTop: 6 }}>
                  Signals seriousness. Held by title/brokerage and credited at closing.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
                  <Slider
                    value={[emdPct]}
                    min={0}
                    max={20}
                    step={1}
                    onValueChange={(v) => setEmdPct(v[0])}
                    className="w-full"
                  />
                  <div style={{ width: 64, textAlign: "right", fontSize: 14 }}>{emdPct}%</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    fontSize: 12,
                    opacity: 0.9,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    className="rounded"
                    style={{
                      background: "rgba(30,64,175,.7)",
                      padding: "4px 8px",
                    }}
                  >
                    2% — Standard
                  </span>
                  <span
                    className="rounded"
                    style={{
                      background: "rgba(109,40,217,.7)",
                      padding: "4px 8px",
                    }}
                  >
                    5% — Strong
                  </span>
                  <span
                    className="rounded"
                    style={{
                      background: "rgba(22,163,74,.7)",
                      padding: "4px 8px",
                    }}
                  >
                    10%+ — Very Strong
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* 6. Inspection */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">6. Home Inspection Contingency</h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {INSPECTION_OPTIONS.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={inspection === opt.id}
                      label={opt.label}
                      onSelect={() => setInspection(opt.id)}
                    />
                  ))}
                </div>

                {inspection === "aLaCarte" && (
                  <div style={{ marginTop: 12 }}>
                    <Label className="text-neutral-200">Pick specific tests (optional)</Label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                        gap: 10,
                        marginTop: 10,
                      }}
                    >
                      {[
                        "Structural & Mechanical",
                        "Mold",
                        "Environmental",
                        "Radon",
                        "Chimney",
                        "Lead-Based Paint",
                        "Wood Destroying Insect",
                      ].map((k) => {
                        const id = k.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                        const checked = inspectionChecks.includes(id);
                        return (
                          <label
                            key={id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 14,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setInspectionChecks((prev) =>
                                  e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                                );
                              }}
                            />
                            <span>{k}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 7. Appraisal */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">7. Appraisal Contingency</h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {APPRAISAL_OPTIONS.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={appraisal === opt.id}
                      label={opt.label}
                      onSelect={() => setAppraisal(opt.id)}
                    />
                  ))}
                </div>
                {appraisal === "gapCover" && (
                  <div style={{ marginTop: 12 }}>
                    <Label className="text-neutral-200">Guarantee to cover appraisal gap up to ($)</Label>
                    <Input
                      inputMode="numeric"
                      value={gapAmount || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setGapAmount(Number(e.target.value.replace(/[^0-9]/g, "")) || 0)
                      }
                      placeholder="e.g., 10000"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 8. Financing Contingency */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">8. Financing Contingency</h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {FINANCING_CONT.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={finCont === opt.id}
                      label={opt.label}
                      onSelect={() => setFinCont(opt.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 9. Recordation / Transfer Tax / Title Company */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">
                  9. Recordation / Transfer Tax / Title Company
                </h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {TITLE_PREF.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={titlePref === opt.id}
                      label={opt.label}
                      onSelect={() =>
                        setTitlePref((prev) => (prev === opt.id ? null : (opt.id as typeof TITLE_PREF[number]["id"])))
                      }
                    />
                  ))}
                  {TAX_TITLE_SPLIT.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={taxSplit === opt.id}
                      label={opt.label}
                      onSelect={() =>
                        setTaxSplit((prev) =>
                          prev === opt.id ? null : (opt.id as typeof TAX_TITLE_SPLIT[number]["id"])
                        )
                      }
                    />
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Tip: Tap again on a selected option to clear it if you’re still deciding.
                </p>
              </CardContent>
            </Card>

            {/* 10. Commission */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">10. Commission</h2>
                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {COMMISSION.map((opt) => (
                    <OptionTile
                      key={opt.id}
                      active={commission === opt.id}
                      label={opt.label}
                      onSelect={() => setCommission(opt.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 11. Escalation */}
            <Card className="mc-card">
              <CardContent className="p-6 md:p-7" style={sectionAnchorStyle}>
                <h2 className="text-lg font-semibold text-violet-200">
                  11. Escalation Clause (Optional)
                </h2>
                <p className="text-sm text-neutral-400" style={{ marginTop: 6 }}>
                  Set the maximum percentage above the next competing offer (if any).
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
                  <Slider
                    value={[escalationPct]}
                    min={0}
                    max={10}
                    step={1}
                    onValueChange={(v) => setEscalationPct(v[0])}
                    className="w-full"
                  />
                  <div style={{ width: 54, textAlign: "right", fontSize: 14 }}>{escalationPct}%</div>
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-xs text-neutral-500" style={{ marginTop: 8 }}>
              © {new Date().getFullYear()} Maison Collective • Built for client education
            </p>
          </div>

          {/* Right: desktop summary */}
          {isWide && (
            <div style={{ position: "sticky", top: 24 }}>
              <OfferStrengthCard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
